import { Audio } from 'expo-av';
import * as Haptics from 'expo-haptics';
import { Platform } from 'react-native';

type IQESHapticCue = 'select' | 'type' | 'swap' | 'submit' | 'warning';
type IQESAudioCue = 'select' | 'submit' | 'correct' | 'verse';

type SensoryOptions = {
  enabled?: boolean;
};

const audioAssets: Record<IQESAudioCue, number> = {
  select: require('../../../assets/iqes/audio/iqes-select.wav'),
  submit: require('../../../assets/iqes/audio/iqes-submit.wav'),
  correct: require('../../../assets/iqes/audio/iqes-correct.wav'),
  verse: require('../../../assets/iqes/audio/iqes-verse.wav'),
};

const audioVolume: Record<IQESAudioCue, number> = {
  select: 0.32,
  submit: 0.34,
  correct: 0.42,
  verse: 0.38,
};

const lastPlayedAt: Partial<Record<IQESAudioCue, number>> = {};
let audioModeReady = false;

const ensureAudioMode = async () => {
  if (audioModeReady || Platform.OS === 'web') return;

  await Audio.setAudioModeAsync({
    playsInSilentModeIOS: false,
    allowsRecordingIOS: false,
    staysActiveInBackground: false,
    shouldDuckAndroid: true,
  });
  audioModeReady = true;
};

const fallbackVibrate = (pattern: number | number[]) => {
  if (typeof navigator !== 'undefined' && 'vibrate' in navigator) {
    navigator.vibrate(pattern);
  }
};

export const playIQESHaptic = async (cue: IQESHapticCue, options: SensoryOptions = {}) => {
  if (options.enabled === false) return;

  try {
    if (Platform.OS === 'web') {
      const pattern = cue === 'warning' ? [80, 40, 80] : cue === 'type' ? 12 : 35;
      fallbackVibrate(pattern);
      return;
    }

    if (cue === 'warning') {
      await Haptics.notificationAsync(Haptics.NotificationFeedbackType.Warning);
      return;
    }

    if (cue === 'submit' || cue === 'swap') {
      await Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium);
      return;
    }

    await Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
  } catch {
    const pattern = cue === 'warning' ? [80, 40, 80] : cue === 'type' ? 12 : 35;
    fallbackVibrate(pattern);
  }
};

export const playIQESAudioCue = async (cue: IQESAudioCue, options: SensoryOptions = {}) => {
  if (options.enabled === false) return;

  const now = Date.now();
  const minGap = cue === 'select' ? 90 : 220;
  if (lastPlayedAt[cue] && now - lastPlayedAt[cue]! < minGap) return;
  lastPlayedAt[cue] = now;

  try {
    await ensureAudioMode();
    const { sound } = await Audio.Sound.createAsync(
      audioAssets[cue],
      {
        shouldPlay: true,
        volume: audioVolume[cue],
        isLooping: false,
      }
    );

    sound.setOnPlaybackStatusUpdate((status) => {
      if ('didJustFinish' in status && status.didJustFinish) {
        void sound.unloadAsync();
      }
    });
  } catch {
    // Audio should never block answer interaction; devices can deny/interrupt playback.
  }
};
